import { useCallback, useEffect, useMemo, useState } from "react";
import {
  AlertTriangle, ArrowLeft, BookOpen, CheckCircle2, CircleUserRound,
  FileClock, FileUp, FolderOpen, LayoutDashboard, LoaderCircle, LogOut, Pencil,
  RefreshCw, Save, Search, Smartphone, X
} from "lucide-react";
import { adminApi } from "./api";
import type { DashboardMetrics, Library, Question } from "./types";

type View = "dashboard" | "libraries";

const STATUS_LABEL = { processing: "解析中", ready: "可练习", failed: "失败" };
const TYPE_LABEL = { single: "单选", multiple: "多选", judge: "判断", fill: "填空", essay: "简答" };

export function App() {
  return window.location.pathname === "/mobile-upload" ? <MobileUpload /> : <AdminApp />;
}

function AdminApp() {
  const [token, setToken] = useState(() => localStorage.getItem("guoer_admin_token") || "");
  const [authenticated, setAuthenticated] = useState(false);
  const [view, setView] = useState<View>("dashboard");
  const [metrics, setMetrics] = useState<DashboardMetrics | null>(null);
  const [libraries, setLibraries] = useState<Library[]>([]);
  const [selectedLibrary, setSelectedLibrary] = useState<Library | null>(null);
  const [questions, setQuestions] = useState<Question[]>([]);
  const [editing, setEditing] = useState<Question | null>(null);
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const loadOverview = useCallback(async (authToken = token) => {
    setLoading(true);
    setError("");
    try {
      const [dashboard, libraryResult] = await Promise.all([
        adminApi<DashboardMetrics>("/dashboard", authToken),
        adminApi<{ items: Library[] }>("/libraries", authToken)
      ]);
      setMetrics(dashboard);
      setLibraries(libraryResult.items);
      setAuthenticated(true);
      localStorage.setItem("guoer_admin_token", authToken);
    } catch (err) {
      setAuthenticated(false);
      setError(err instanceof Error ? err.message : "加载失败");
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { if (token) void loadOverview(token); }, []);

  const login = (event: React.FormEvent) => {
    event.preventDefault();
    void loadOverview(token);
  };

  const logout = () => {
    localStorage.removeItem("guoer_admin_token");
    setAuthenticated(false);
    setToken("");
  };

  const openLibrary = async (library: Library) => {
    setLoading(true);
    setError("");
    try {
      const result = await adminApi<{ items: Question[] }>(`/libraries/${library.id}/questions`, token);
      setSelectedLibrary(library);
      setQuestions(result.items);
    } catch (err) {
      setError(err instanceof Error ? err.message : "加载题目失败");
    } finally { setLoading(false); }
  };

  const saveQuestion = async (question: Question) => {
    setLoading(true);
    try {
      await adminApi(`/questions/${question.id}`, token, {
        method: "PUT",
        body: JSON.stringify({ ...question, needsReview: false })
      });
      setQuestions((items) => items.map((item) => item.id === question.id ? { ...question, needsReview: false, confidence: 1 } : item));
      setLibraries((items) => items.map((item) => item.id === selectedLibrary?.id
        ? { ...item, reviewCount: Math.max(0, item.reviewCount - (editing?.needsReview ? 1 : 0)) }
        : item));
      setEditing(null);
      await loadOverview();
    } catch (err) { setError(err instanceof Error ? err.message : "保存失败"); }
    finally { setLoading(false); }
  };

  const filtered = useMemo(() => {
    const normalized = query.trim().toLowerCase();
    if (!normalized) return libraries;
    return libraries.filter((item) => `${item.title} ${item.sourceFilename} ${item.owner}`.toLowerCase().includes(normalized));
  }, [libraries, query]);

  if (!authenticated) return <Login token={token} setToken={setToken} submit={login} loading={loading} error={error} />;

  return (
    <div className="app-shell">
      <aside className="sidebar">
        <div className="brand-block"><div className="brand-mark">过</div><div><strong>过儿刷刷题</strong><span>运营管理台</span></div></div>
        <nav>
          <button className={view === "dashboard" ? "active" : ""} onClick={() => { setView("dashboard"); setSelectedLibrary(null); }}><LayoutDashboard size={18}/>总览</button>
          <button className={view === "libraries" ? "active" : ""} onClick={() => { setView("libraries"); setSelectedLibrary(null); }}><BookOpen size={18}/>题库审核{metrics?.pendingReview ? <b>{metrics.pendingReview}</b> : null}</button>
        </nav>
        <button className="logout" onClick={logout}><LogOut size={17}/>退出管理台</button>
      </aside>

      <main className="main-area">
        {error && <div className="error-banner"><AlertTriangle size={17}/>{error}<button onClick={() => setError("")}><X size={16}/></button></div>}
        {selectedLibrary ? (
          <QuestionReview
            library={selectedLibrary}
            questions={questions}
            onBack={() => setSelectedLibrary(null)}
            onEdit={setEditing}
          />
        ) : view === "dashboard" ? (
          <Dashboard metrics={metrics} libraries={libraries} refresh={() => loadOverview()} loading={loading} openLibrary={openLibrary}/>
        ) : (
          <LibraryTable items={filtered} query={query} setQuery={setQuery} openLibrary={openLibrary} refresh={() => loadOverview()} loading={loading}/>
        )}
      </main>
      {editing && <QuestionEditor question={editing} close={() => setEditing(null)} save={saveQuestion} saving={loading}/>} 
    </div>
  );
}

type ImportState = "checking" | "ready" | "uploading" | "processing" | "completed" | "error";

function MobileUpload() {
  const params = useMemo(() => new URLSearchParams(window.location.hash.slice(1)), []);
  const ticket = params.get("ticket") || "";
  const apiBase = (params.get("api") || window.location.origin).replace(/\/$/, "");
  const isWechatBrowser = /MicroMessenger/i.test(window.navigator.userAgent);
  const [state, setState] = useState<ImportState>("checking");
  const [file, setFile] = useState<File | null>(null);
  const [title, setTitle] = useState("");
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState("");

  const loadStatus = useCallback(async () => {
    if (!ticket) throw new Error("导入链接缺少票据信息");
    const response = await fetch(`${apiBase}/api/import-tickets/public/status`, { headers: { "x-import-ticket": ticket } });
    const body = await response.json().catch(() => ({})) as { status?: ImportState | "pending" | "failed" | "expired"; progress?: number; error?: string; message?: string };
    if (!response.ok) throw new Error(body.message || "导入链接不可用");
    if (body.status === "completed") { setProgress(100); setState("completed"); return; }
    if (body.status === "failed" || body.status === "expired") throw new Error(body.error || (body.status === "expired" ? "导入链接已过期" : "解析未完成"));
    if (["uploading", "processing"].includes(body.status || "")) {
      setProgress(Math.max(50, Number(body.progress || 0)));
      setState("processing");
      return;
    }
    setState("ready");
  }, [apiBase, ticket]);

  useEffect(() => {
    document.title = "上传手机文件 - 过儿刷刷题";
    void loadStatus().catch((err) => { setError(err instanceof Error ? err.message : "链接不可用"); setState("error"); });
  }, [loadStatus]);

  useEffect(() => {
    if (state !== "processing") return undefined;
    const timer = window.setInterval(() => {
      void loadStatus().catch((err) => { setError(err instanceof Error ? err.message : "查询失败"); setState("error"); });
    }, 1200);
    return () => window.clearInterval(timer);
  }, [loadStatus, state]);

  const choose = (event: React.ChangeEvent<HTMLInputElement>) => {
    const next = event.target.files?.[0] || null;
    if (!next) return;
    if (next.size > 50 * 1024 * 1024) {
      setError("文件不能超过 50MB");
      event.target.value = "";
      return;
    }
    setError("");
    setFile(next);
    setTitle(next.name.replace(/\.[^.]+$/, "").slice(0, 80));
  };

  const upload = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!file || !title.trim()) return;
    setState("uploading");
    setProgress(24);
    setError("");
    const form = new FormData();
    form.append("title", title.trim());
    form.append("file", file);
    try {
      const response = await fetch(`${apiBase}/api/import-tickets/public/upload`, { method: "POST", headers: { "x-import-ticket": ticket }, body: form });
      const body = await response.json().catch(() => ({})) as { message?: string };
      if (!response.ok) throw new Error(body.message || "上传失败");
      setProgress(50);
      setState("processing");
    } catch (err) {
      setError(err instanceof Error ? err.message : "上传失败");
      setState("error");
    }
  };

  const close = () => {
    const bridge = (window as Window & { wx?: { miniProgram?: { navigateBack: () => void } } }).wx;
    if (bridge?.miniProgram) bridge.miniProgram.navigateBack();
    else window.history.back();
  };

  return <main className="mobile-import">
    <header className="mobile-brand"><span>过</span><div><strong>过儿刷刷题</strong><small>一次性安全上传</small></div></header>
    <section className="mobile-import-main">
      <div className="mobile-title"><Smartphone size={22}/><div><h1>上传手机中的资料</h1><p>支持 PDF、Word、Excel、CSV 和 TXT，最大 50MB</p></div></div>

      {isWechatBrowser && <div className="browser-hint"><AlertTriangle size={18}/><div><strong>建议在系统浏览器打开</strong><span>点右上角菜单，选择“在浏览器打开”，文件选择更稳定。</span></div></div>}

      {state === "checking" && <div className="mobile-status"><LoaderCircle className="spin"/><strong>正在验证导入链接</strong></div>}

      {(state === "ready" || (state === "error" && file)) && <form onSubmit={upload}>
        <label className={`mobile-file-picker ${file ? "selected" : ""}`}>
          <input type="file" accept=".pdf,.doc,.docx,.xls,.xlsx,.csv,.txt" onChange={choose}/>
          {file ? <><FolderOpen size={34}/><strong>{file.name}</strong><span>{(file.size / 1024 / 1024).toFixed(2)} MB</span></> : <><FileUp size={36}/><strong>选择手机中的资料</strong><span>iPhone 从“文件”选择，安卓从文件管理器选择</span></>}
        </label>
        {file && <label className="mobile-field"><span>题库名称</span><input value={title} maxLength={80} onChange={(event) => setTitle(event.target.value)} /></label>}
        {error && <div className="mobile-error"><AlertTriangle size={17}/>{error}</div>}
        <button className="mobile-submit" disabled={!file || !title.trim()}><FileUp size={18}/>上传并开始解析</button>
      </form>}

      {(state === "uploading" || state === "processing") && <div className="mobile-progress">
        <div className="progress-icon"><LoaderCircle className="spin" size={26}/></div>
        <strong>{state === "uploading" ? "正在上传资料" : "正在识别题目"}</strong>
        <span>{state === "uploading" ? "文件上传完成后会自动开始解析" : "正在拆分题干、选项、答案和解析"}</span>
        <div className="mobile-progress-row"><div><i style={{ width: `${progress}%` }}/></div><b>{progress}%</b></div>
      </div>}

      {state === "completed" && <div className="mobile-complete"><CheckCircle2 size={42}/><h2>题库已经就绪</h2><p>请返回微信，过儿刷刷题会自动打开生成的题库</p><button onClick={close}>返回上一页</button></div>}
      {state === "error" && !file && <div className="mobile-complete error"><AlertTriangle size={42}/><h2>暂时无法导入</h2><p>{error}</p><button onClick={close}>返回小程序</button></div>}
    </section>
    <footer className="mobile-privacy">文件仅用于生成你的私人题库</footer>
  </main>;
}

function Login({ token, setToken, submit, loading, error }: {
  token: string; setToken: (value: string) => void; submit: (event: React.FormEvent) => void; loading: boolean; error: string;
}) {
  return <div className="login-page">
    <form className="login-panel" onSubmit={submit}>
      <div className="brand-mark large">过</div>
      <h1>过儿刷刷题</h1><p>运营管理台</p>
      <label htmlFor="token">管理令牌</label>
      <input id="token" type="password" autoComplete="current-password" value={token} onChange={(e) => setToken(e.target.value)} placeholder="输入 ADMIN_TOKEN" />
      {error && <div className="login-error">{error}</div>}
      <button className="primary" disabled={loading || token.length < 12}>{loading ? "正在验证" : "进入管理台"}</button>
    </form>
  </div>;
}

function Dashboard({ metrics, libraries, refresh, loading, openLibrary }: {
  metrics: DashboardMetrics | null; libraries: Library[]; refresh: () => void; loading: boolean; openLibrary: (library: Library) => void;
}) {
  const cards = [
    ["待校对题目", metrics?.pendingReview ?? 0, AlertTriangle, "warning"],
    ["题目总数", metrics?.questions ?? 0, BookOpen, "green"],
    ["累计答题", metrics?.attempts ?? 0, CheckCircle2, "blue"],
    ["用户", metrics?.users ?? 0, CircleUserRound, "neutral"]
  ] as const;
  return <>
    <PageHeader title="运营总览" subtitle="解析质量、任务状态与学习活动" refresh={refresh} loading={loading}/>
    <section className="metric-grid">{cards.map(([label, value, Icon, tone]) => <div className={`metric ${tone}`} key={label}><div><span>{label}</span><strong>{value.toLocaleString()}</strong></div><Icon size={21}/></div>)}</section>
    <section className="work-section">
      <div className="section-heading"><div><h2>需要处理</h2><p>优先检查失败任务和低置信度题目</p></div><span>{metrics?.activeJobs ?? 0} 个任务进行中</span></div>
      <LibraryRows items={libraries.filter((item) => item.status === "failed" || item.reviewCount > 0).slice(0, 8)} openLibrary={openLibrary}/>
    </section>
  </>;
}

function PageHeader({ title, subtitle, refresh, loading }: { title: string; subtitle: string; refresh: () => void; loading: boolean }) {
  return <header className="page-header"><div><h1>{title}</h1><p>{subtitle}</p></div><button className="icon-button" onClick={refresh} title="刷新"><RefreshCw size={18} className={loading ? "spin" : ""}/></button></header>;
}

function LibraryTable({ items, query, setQuery, openLibrary, refresh, loading }: {
  items: Library[]; query: string; setQuery: (value: string) => void; openLibrary: (library: Library) => void; refresh: () => void; loading: boolean;
}) {
  return <>
    <PageHeader title="题库审核" subtitle="校对识别结果并处理失败任务" refresh={refresh} loading={loading}/>
    <div className="toolbar"><Search size={17}/><input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="搜索题库、文件或用户"/></div>
    <section className="work-section flush"><LibraryRows items={items} openLibrary={openLibrary}/></section>
  </>;
}

function LibraryRows({ items, openLibrary }: { items: Library[]; openLibrary: (library: Library) => void }) {
  if (!items.length) return <div className="empty-state"><CheckCircle2 size={26}/><strong>当前没有待处理内容</strong></div>;
  return <div className="table-wrap"><table><thead><tr><th>题库</th><th>用户</th><th>题目</th><th>校对</th><th>状态</th><th>更新时间</th><th></th></tr></thead><tbody>
    {items.map((item) => <tr key={item.id}>
      <td><strong>{item.title}</strong><span>{item.sourceFilename}</span>{item.error && <em>{item.error}</em>}</td>
      <td>{item.owner || "未命名"}</td><td>{item.questionCount}</td><td><b className={item.reviewCount ? "count-alert" : ""}>{item.reviewCount}</b></td>
      <td><span className={`status ${item.status}`}>{STATUS_LABEL[item.status]}</span></td><td>{new Date(item.updatedAt).toLocaleString("zh-CN", { month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit" })}</td>
      <td><button className="text-button" disabled={!item.questionCount} onClick={() => openLibrary(item)}>查看</button></td>
    </tr>)}
  </tbody></table></div>;
}

function QuestionReview({ library, questions, onBack, onEdit }: { library: Library; questions: Question[]; onBack: () => void; onEdit: (question: Question) => void }) {
  return <>
    <header className="review-header"><button className="icon-button" onClick={onBack} title="返回"><ArrowLeft size={18}/></button><div><div className="review-title-row"><h1>{library.title}</h1><span className={`status ${library.status}`}>{STATUS_LABEL[library.status]}</span></div><p>{library.sourceFilename} · {questions.length} 题</p></div></header>
    <div className="review-summary"><span><AlertTriangle size={16}/>{questions.filter((item) => item.needsReview).length} 题待校对</span><span>低于 80% 置信度的题目会自动进入队列</span></div>
    <section className="question-list">
      {questions.map((question) => <article className={`question-row ${question.needsReview ? "needs-review" : ""}`} key={question.id}>
        <div className="question-number">{question.orderNo}</div>
        <div className="question-content"><div className="question-tags"><span>{TYPE_LABEL[question.type]}</span><span>置信度 {Math.round(question.confidence * 100)}%</span>{question.needsReview && <strong>待校对</strong>}</div><h3>{question.stem}</h3>{question.options.length > 0 && <div className="option-preview">{question.options.map((option) => <span key={option.key}><b>{option.key}</b>{option.text}</span>)}</div>}<div className="answer-preview">答案：{question.answer.join("、") || "未识别"}</div></div>
        <button className="icon-button edit" onClick={() => onEdit(question)} title="编辑题目"><Pencil size={17}/></button>
      </article>)}
    </section>
  </>;
}

function QuestionEditor({ question, close, save, saving }: { question: Question; close: () => void; save: (question: Question) => void; saving: boolean }) {
  const [draft, setDraft] = useState(question);
  const optionText = draft.options.map((option) => `${option.key}. ${option.text}`).join("\n");
  const setOptions = (value: string) => setDraft({ ...draft, options: value.split("\n").flatMap((line) => { const match = line.match(/^\s*([A-H])\s*[.、:：)]\s*(.+)$/i); return match?.[1] && match[2] ? [{ key: match[1].toUpperCase(), text: match[2].trim() }] : []; }) });
  return <div className="drawer-backdrop" role="presentation" onMouseDown={(e) => e.target === e.currentTarget && close()}><aside className="editor-drawer" role="dialog" aria-modal="true" aria-label="编辑题目"><header><div><h2>校对第 {draft.orderNo} 题</h2><p>{draft.sourceRef || "来源位置未记录"}</p></div><button className="icon-button" onClick={close} title="关闭"><X size={18}/></button></header><div className="editor-body">
    <label>题型<select value={draft.type} onChange={(e) => setDraft({ ...draft, type: e.target.value as Question["type"] })}>{Object.entries(TYPE_LABEL).map(([value, label]) => <option value={value} key={value}>{label}</option>)}</select></label>
    <label>题干<textarea rows={6} value={draft.stem} onChange={(e) => setDraft({ ...draft, stem: e.target.value })}/></label>
    {!['judge','fill','essay'].includes(draft.type) && <label>选项<textarea rows={7} value={optionText} onChange={(e) => setOptions(e.target.value)} placeholder="A. 选项内容"/></label>}
    <label>答案<input value={draft.answer.join(",")} onChange={(e) => setDraft({ ...draft, answer: e.target.value.split(/[,，]/).map((item) => item.trim()).filter(Boolean) })}/></label>
    <label>解析<textarea rows={5} value={draft.explanation} onChange={(e) => setDraft({ ...draft, explanation: e.target.value })}/></label>
    <label>知识点<input value={draft.tags.join(",")} onChange={(e) => setDraft({ ...draft, tags: e.target.value.split(/[,，]/).map((item) => item.trim()).filter(Boolean) })}/></label>
  </div><footer><button className="secondary" onClick={close}>取消</button><button className="primary save" disabled={saving || !draft.stem.trim() || !draft.answer.length} onClick={() => save(draft)}><Save size={17}/>{saving ? "保存中" : "保存并通过"}</button></footer></aside></div>;
}
